import { renderNotice } from '../lib/notices.js'
import { track } from '../track.js'

const TIER_STAMP = {
  1: { label: 'All clear', color: 'text-go-green border-go-green' },
  2: { label: 'Some prep', color: 'text-prep-amber border-prep-amber' },
  3: { label: 'Visa appt', color: 'text-visa-coral border-visa-coral' },
}

// DISPLAY ONLY, and only for TOTALS. A trip total's trailing digits imply
// precision the model does not have: the flight term is a flat distance-band
// constant (engine.FLIGHT_BANDS is a 4-value step function), so "$1,610/pp"
// reads as four significant figures derived from one of four numbers. $50 is
// ~1.2% of a typical total — the USD counterpart of the MP's ¥100 rounding
// (miniapp/src/config/index.js roundCny100), and kept in step with it.
//
// NOT applied to the component line items (flight / lodging / daily living):
// $50 on a tier-1 daily-living figure would distort it badly, and those come
// from real inputs (nights, rooms). NOT applied to over_by either — a $5
// overage would render as "~$0 over". Components therefore stay exact and may
// not visibly sum to the rounded total; the gap is at most $25 and every
// figure already carries "~".
const totalUsd = (usd) => (Math.round(usd / 50) * 50).toLocaleString()

// Plain-language requirement names (no internal scores or codes).
const REQ_LABEL = {
  self: 'Visa-free',
  visa_free: 'Visa-free',
  visa_on_arrival: 'Visa on arrival',
  eta: 'ETA',
  e_visa: 'eVisa',
  visa_required: 'Embassy visa',
}

// Card-face status describing who needs what, e.g.
// "Visa-free for everyone", "eVisa for 2 people", "Embassy visa for 1 person".
function visaStatus(group, members) {
  const req = group.requirement
  if (req === 'unknown') return 'No visa data for this destination'
  const label = REQ_LABEL[req]
  if (!label) return null
  // self + visa_free share a label; count members at the binding requirement.
  const count = members.filter((m) => REQ_LABEL[m.requirement] === label).length
  if (count >= members.length) return `${label} for everyone`
  const who = count === 1 ? '1 person' : `${count} people`
  return `${label} for ${who}`
}

function Stamp({ tier }) {
  if (!tier) return null
  const { label, color } = TIER_STAMP[tier] || {}
  return (
    <span
      className={`inline-block shrink-0 whitespace-nowrap rounded border-2 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${color}`}
      style={{ transform: 'rotate(-2deg)' }}
    >
      {label}
    </span>
  )
}

// Budget band shown on feasible cards — budget never excludes, only flags.
// Null band (no budget given) renders nothing.
function BudgetBand({ cost }) {
  const band = cost.budget_band
  // A budget was given but the total has no flight in it, so there is nothing
  // honest to band against. Say so rather than render nothing: silence here
  // looks identical to "no budget was set".
  if (!band && cost.budget_band_unavailable) {
    return <span className="text-xs font-medium text-slate-400">Budget not checked</span>
  }
  if (!band) return null
  if (band === 'within') {
    return <span className="text-xs font-medium text-go-green">Within budget</span>
  }
  const amount = `~$${(cost.over_by || 0).toLocaleString()} over`
  if (band === 'slightly_over') {
    return <span className="text-xs font-medium text-prep-amber">{amount} (slightly)</span>
  }
  return <span className="text-xs font-medium text-visa-coral">{amount} budget</span>
}

const ENTRY_BUTTON_LABEL = {
  evisa: 'Apply for eVisa',
  eta: 'Apply for eTA',
  arrival_registration: 'Arrival registration required',
  visa_free_registration: 'Entry registration required',
  online_registration: 'Apply online',
}

// When a traveller already has an unmet primary requirement (e.g. an eVisa we
// can't link), any mandatory arrival form is reframed as a secondary step so it
// never reads as the whole answer: "Also required: arrival registration →".
const ENTRY_SECONDARY_LABEL = {
  arrival_registration: 'arrival registration',
  visa_free_registration: 'entry registration',
}

// Honest notice for a member whose online authorization (eVisa/eTA) has no
// verified portal in our data. It is a statement about OUR data — never a fake
// button, never invented embassy advice unless the data explicitly says so.
function EntryNotice({ notice }) {
  if (!notice?.length) return null
  return (
    <div className="mt-2.5 space-y-2">
      {notice.map((n, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-xs font-semibold text-slate-700">
            {REQ_LABEL[n.requirement] || n.label} needed
            {n.names?.length > 0 && (
              <span className="font-normal text-slate-500"> · {n.names.join(', ')}</span>
            )}
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{renderNotice(n)}</p>
        </div>
      ))}
    </div>
  )
}

// Informational transit note (e.g. China 240h visa-free transit). Purely
// informational — the engine never lets it change a member's requirement,
// tier, or feasibility, so it renders as a neutral note, never a button.
function TransitNotes({ notes }) {
  if (!notes?.length) return null
  return (
    <div className="mt-2.5 space-y-2">
      {notes.map((n, i) => (
        <div key={i} className="rounded-lg border border-ocean/30 bg-ocean/5 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ocean">
            Transit option
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-slate-600">{renderNotice(n)}</p>
          {n.applies_to?.length > 0 && (
            <p className="mt-1 text-[10px] text-slate-400">For {n.applies_to.join(', ')}</p>
          )}
        </div>
      ))}
    </div>
  )
}

// Verified official entry links (BUILD 1 + full dataset). A destination may
// attach several (e.g. eVisa + mandatory arrival form), so render each as its
// own button. Caption distinguishes a government site from an authorized
// partner. Only rendered when the engine attached a link (someone needs it).
// `hasUnmet` reframes mandatory arrival/entry forms as a secondary step so a
// form is never presented as the complete answer to an unmet visa requirement.
// Consistent applicability line: whenever a link does not apply to the whole
// group, state exactly who it applies to. (needed_by lists the members who need
// the link; a member who is neither in needed_by nor exempt simply doesn't need
// it — e.g. a citizen of the destination.)
function applicabilityLine(link, memberCount) {
  const needed = link.needed_by || []
  if (needed.length && memberCount > 1 && needed.length < memberCount) {
    return `Only for ${needed.join(', ')}`
  }
  if (link.exempt_names?.length) {
    return `Not required for ${link.exempt_names.join(', ')}`
  }
  return null
}

function EntryLinks({ links, hasUnmet, memberCount, dest }) {
  if (!links?.length) return null
  return (
    <div className="mt-2.5 space-y-2.5">
      {links.map((link, i) => {
        const isReg = link.category === 'arrival_registration'
        let buttonLabel
        if (link.optional_prearrival) {
          buttonLabel = 'Apply online in advance (optional)'
        } else if (hasUnmet && isReg) {
          buttonLabel = `Also required: ${ENTRY_SECONDARY_LABEL[link.entry_type] || 'registration'}`
        } else {
          buttonLabel = link.label_override || ENTRY_BUTTON_LABEL[link.entry_type] || 'Apply online'
        }
        const applies = applicabilityLine(link, memberCount)
        return (
          <div key={i}>
            <a
              href={link.official_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('portal_click', { dest, kind: link.entry_type })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-ocean px-3 py-2 text-xs font-semibold text-white active:opacity-80"
            >
              {buttonLabel}
              <span aria-hidden="true">↗</span>
            </a>
            <p className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">
              {link.caption || 'official government site'}
            </p>
            {applies && <p className="mt-1 text-xs text-slate-500">{applies}</p>}
          </div>
        )
      })}
    </div>
  )
}

export default function DestinationCard({ row, onRefineOrigin, highlight = false }) {
  const { jurisdiction, group, members, cost, tier, verdict, caveats } = row
  // Render per-member reasons from the structured codes (localisable); fall
  // back to the deprecated English strings if the structured field is absent.
  const reasons = (row.exclusion_notices?.length
    ? row.exclusion_notices.map(renderNotice)
    : row.exclusion_reasons) || []
  const isExcluded = verdict === 'excluded'
  const status = visaStatus(group, members)
  const isRoughEstimate = cost.tier_basis === 'continent_default'
  const displayCaveats = caveats || []
  // THE flag for the no-origin state, read from the payload rather than
  // inferred by string-matching caveat prose. "unknown" only when NO member
  // gave a departure airport; a PARTIAL group is "group_average" and renders
  // normally, because its estimate is grounded in a real distance.
  const flightUnknown = cost.flight.basis === 'unknown'

  return (
    <li
      // Scroll target for a destination the user named in the search box.
      id={`dest-${jurisdiction.iso2}`}
      className={`overflow-hidden rounded-xl border bg-white ${
        highlight ? 'border-horizon ring-2 ring-horizon/40' : 'border-slate-200'
      }`}
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
        <div className="min-w-0">
          <h3 className="font-display text-xl font-semibold leading-tight text-slate-900 truncate">
            {jurisdiction.name}
          </h3>
          {/* Space Mono data row — anchor + cost, no internal scores */}
          <p className="mt-1 font-mono text-[11px] text-slate-500 leading-loose">
            {cost.anchor_iata}
            {'  ·  '}
            ~${totalUsd(cost.per_person_usd)}/pp
            {/* The face figure excludes the flight when no member gave an
                origin, and the face is where someone decides whether a
                destination is affordable. Unlabelled it reads as a full
                trip price, low by a whole long-haul flight. */}
            {flightUnknown && ' excl. flight'}
            {isRoughEstimate && (
              <span className="text-slate-400">{'  ·  '}rough estimate</span>
            )}
          </p>
          {/* Plain-language visa status */}
          {status && (
            <p className="mt-1 text-xs font-medium text-slate-700">{status}</p>
          )}
          {/* Budget band (feasible rows) — never excludes, just flags */}
          {!isExcluded && (cost.budget_band || cost.budget_band_unavailable) && (
            <p className="mt-0.5">
              <BudgetBand cost={cost} />
            </p>
          )}
          {/* Entry requirements: unmet primary requirement first (honest notice
              when we have no verified portal), then the verified links — any
              mandatory arrival form reframed as a secondary step. */}
          {!isExcluded && (
            <>
              <EntryNotice notice={row.entry_notice} />
              <EntryLinks
                links={row.entry_links}
                hasUnmet={(row.entry_notice?.length || 0) > 0}
                memberCount={members.length}
                dest={jurisdiction.iso2}
              />
              <TransitNotes notes={row.transit_notes} />
            </>
          )}
        </div>
        <div className="shrink-0 pt-1 text-right">
          {isExcluded ? (
            <span className="font-mono text-[10px] uppercase tracking-widest text-visa-coral">
              Travel ban
            </span>
          ) : (
            <Stamp tier={tier} />
          )}
        </div>
      </div>

      {/* Excluded: reasons */}
      {isExcluded && reasons.length > 0 && (
        <ul className="border-t border-slate-100 px-4 py-2 space-y-0.5">
          {reasons.map((r, i) => (
            <li key={i} className="text-xs text-slate-500 leading-relaxed">
              <span className="mr-1.5 text-slate-300">✕</span>{r}
            </li>
          ))}
        </ul>
      )}

      {/* Feasible: perforated divider + expandable breakdown */}
      {!isExcluded && (
        <details>
          {/* The dashed line IS the summary — boarding-pass tear-off edge */}
          <summary className="flex cursor-pointer select-none list-none items-center gap-2 border-t-2 border-dashed border-slate-200 px-4 py-2 [&::-webkit-details-marker]:hidden">
            <span className="h-px flex-1 bg-transparent" />
            <span className="font-mono text-[10px] text-slate-400 whitespace-nowrap">▸ per person</span>
            <span className="h-px flex-1 bg-transparent" />
          </summary>

          <div className="pb-3">
            {/* Visa notes for tier-3 (who needs to apply) */}
            {reasons.length > 0 && (
              <ul className="px-4 pt-2 pb-1 space-y-1">
                {reasons.map((r, i) => (
                  <li key={i} className="text-xs text-visa-coral leading-relaxed">
                    <span className="mr-1.5">→</span>{r}
                  </li>
                ))}
              </ul>
            )}

            {/* Per-member breakdown */}
            <ul className="divide-y divide-slate-50 mt-1">
              {members.map((m, i) => (
                <li key={i} className="flex items-baseline justify-between gap-2 px-4 py-1.5">
                  <span className="min-w-0">
                    <span className="text-xs font-medium text-slate-800">{m.name}</span>
                    {(m.permit_used || m.passport_used) && (
                      <span className="ml-1 font-mono text-[10px] text-slate-400">
                        {m.permit_used ? `(${m.permit_used} permit)` : `(${m.passport_used})`}
                      </span>
                    )}
                    {m.permit_conditions && (
                      <span
                        className="mt-0.5 block text-[10px] leading-snug text-slate-400"
                        title={m.permit_conditions}
                      >
                        {m.permit_conditions}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 font-mono text-[11px] text-slate-500">
                    {m.label || m.requirement}
                    {typeof m.days_allowed === 'number' && ` · ${m.days_allowed} days`}
                    {m.trip_cost_usd != null && ` · $${totalUsd(m.trip_cost_usd)}`}
                  </span>
                </li>
              ))}
            </ul>

            {/* Cost breakdown: flight + lodging + daily living */}
            <dl className="mx-4 mt-2 space-y-0.5 font-mono text-[10px] text-slate-500">
              {/* With no origin the flight is not estimated and not in the
                  total, so the row says so and offers the field that fixes it.
                  It never shows a number: any figure here would be invented,
                  and a greyed or struck-through one still reads as a price. */}
              <div className="flex justify-between">
                <dt>{flightUnknown ? 'Flight' : `Flight (${cost.anchor_iata})`}</dt>
                {flightUnknown ? (
                  <dd>
                    Not counted
                    {onRefineOrigin && (
                      <button
                        type="button"
                        onClick={onRefineOrigin}
                        className="ml-1.5 underline decoration-dotted underline-offset-2 hover:text-ocean"
                      >
                        Set departure airport
                      </button>
                    )}
                  </dd>
                ) : (
                  <dd>~${cost.flight.per_person_usd.toLocaleString()}/pp</dd>
                )}
              </div>
              <div className="flex justify-between">
                <dt>
                  Lodging ${cost.lodging.nightly_usd}/night × {cost.lodging.nights}n ×{' '}
                  {cost.lodging.rooms} {cost.lodging.rooms === 1 ? 'room' : 'rooms'}
                </dt>
                <dd>~${cost.lodging.per_person_usd.toLocaleString()}/pp</dd>
              </div>
              <div className="flex justify-between">
                <dt>Daily living ${cost.living.daily_usd}/day × {cost.living.days}d</dt>
                <dd>~${cost.living.per_person_usd.toLocaleString()}/pp</dd>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-0.5 font-bold text-slate-600">
                <dt>Total</dt>
                <dd>~${totalUsd(cost.per_person_usd)}/pp</dd>
              </div>
            </dl>

            {/* Caveats */}
            {displayCaveats.length > 0 && (
              <ul className="mx-4 mt-1.5 space-y-0.5">
                {displayCaveats.map((c, i) => (
                  <li key={i} className="text-[11px] text-prep-amber leading-relaxed">
                    <span className="mr-1">⚠</span>
                    {/* The no-origin caveat is a one-tap nudge: opens the form's
                        refine section focused on the departure-airport field.
                        Gated on the PAYLOAD FLAG, not the caveat prose - the
                        MP's equivalent sniffing was the fragility this change
                        removed, and the engine now states this outright. The
                        flag picks the state; the substring only picks WHICH of
                        this row's caveat lines carries the tap. */}
                    {flightUnknown && c.includes('flight not included') && onRefineOrigin ? (
                      <button
                        type="button"
                        onClick={onRefineOrigin}
                        className="text-left underline decoration-dotted underline-offset-2"
                      >
                        {c}
                      </button>
                    ) : (
                      c
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </details>
      )}
    </li>
  )
}
